import * as React from 'react';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../core/components/Button.tsx';
import Checkbox from '../../../../core/components/Checkbox.tsx';
import SelectField from '../../../../core/components/Fields/SelectField.tsx';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import Modal from '../../../../core/components/Modal.tsx';
import TargetFieldSelect from './TargetFieldSelect.tsx';
import { useOutputMapperEditor } from '../OutputMapperEditorProvider.tsx';
import type { FieldDefinition, FieldType, TypeDefinition } from '../types.ts';

interface AddFieldDialogProps {
    mode: 'output' | 'custom_input';
    onClose: () => void;
}

const AddFieldDialog = ({ mode, onClose }: AddFieldDialogProps) => {
    const { t } = useTranslation('integrations');
    const { config, addMapping, addCustomField, fieldTypes } = useOutputMapperEditor();

    const [fieldName, setFieldName] = useState('');
    const [fieldType, setFieldType] = useState<FieldType>('string');
    const [error, setError] = useState<string | null>(null);
    const [fieldDescription, setFieldDescription] = useState('');
    const [isArray, setIsArray] = useState(false);
    const [selectedPath, setSelectedPath] = useState<string>('');

    const pathOptions = useMemo(() => {
        if (mode !== 'custom_input') return [];
        const options: { value: string; label: string; typeName: string; fieldPath: string[] }[] = [];
        const addPaths = (type: TypeDefinition, prefix: string, fieldPath: string[]) => {
            options.push({ value: prefix, label: prefix, typeName: type.name, fieldPath });
            const addNested = (fields: FieldDefinition[], currentPrefix: string, currentPath: string[]) => {
                fields.forEach((field) => {
                    if (field.type === 'object') {
                        const newPrefix = `${currentPrefix}.${field.name}`;
                        const newPath = [...currentPath, field.name];
                        options.push({ value: newPrefix, label: newPrefix, typeName: type.name, fieldPath: newPath });
                        if (field.nested_fields) addNested(field.nested_fields, newPrefix, newPath);
                    }
                });
            };
            addNested(type.fields, prefix, fieldPath);
        };
        config.input_types.forEach((type) => addPaths(type, type.name, []));
        return options;
    }, [mode, config.input_types]);

    const selectedPathOption = useMemo(
        () => pathOptions.find((opt) => opt.value === selectedPath),
        [pathOptions, selectedPath]
    );

    const existingNames = useMemo(() => {
        if (mode === 'output') {
            return new Set(config.output_mapping.map((m) => m.target));
        }
        if (!selectedPathOption) return new Set<string>();
        const type = config.input_types.find((t) => t.name === selectedPathOption.typeName);
        if (!type) return new Set<string>();
        let fields: FieldDefinition[] = type.fields;
        for (const segment of selectedPathOption.fieldPath) {
            const field = fields.find((f) => f.name === segment);
            if (!field) return new Set<string>();
            fields = field.nested_fields || [];
        }
        return new Set(fields.map((f) => f.name));
    }, [mode, config, selectedPathOption]);

    const targetOptions = useMemo(
        () =>
            mode === 'output'
                ? (config.target_field_options ?? []).filter(
                      (option) => !option.array_target && !existingNames.has(option.target)
                  )
                : [],
        [mode, config.target_field_options, existingNames]
    );

    React.useEffect(() => {
        if (mode === 'custom_input' && pathOptions.length > 0 && !selectedPath) {
            setSelectedPath(pathOptions[0].value);
        }
    }, [mode, pathOptions, selectedPath]);

    const validate = (): string | null => {
        if (!fieldName.trim()) {
            return t(
                mode === 'output'
                    ? 'outputMapper.visualEditor.fieldTargetRequired'
                    : 'outputMapper.visualEditor.fieldNameRequired'
            );
        }
        if (mode === 'custom_input' && !/^[a-zA-Z][a-zA-Z0-9_]*$/.test(fieldName)) {
            return t('outputMapper.visualEditor.fieldNameInvalid');
        }
        if (existingNames.has(fieldName)) {
            return t('outputMapper.visualEditor.fieldAlreadyExists');
        }
        if (mode === 'custom_input' && !selectedPathOption) {
            return t('outputMapper.visualEditor.selectParentPathRequired');
        }
        return null;
    };

    const handleSubmit = () => {
        const err = validate();
        if (err) {
            setError(err);
            return;
        }

        if (mode === 'output') {
            addMapping(fieldName.trim());
        } else {
            const newField: FieldDefinition = { name: fieldName.trim(), type: fieldType, is_custom: true };
            if (fieldDescription.trim()) newField.description = fieldDescription.trim();
            if (isArray) newField.is_array = true;
            addCustomField(selectedPathOption!.typeName, selectedPathOption!.fieldPath, newField);
        }
        onClose();
    };

    const title = t(
        mode === 'output' ? 'outputMapper.visualEditor.addField' : 'outputMapper.visualEditor.addCustomField'
    );

    return (
        <Modal
            visible
            onClose={onClose}
            title={title}
            className="!w-[500px]"
            buttons={
                <>
                    <Button variant="outline" onClick={onClose} type="button">
                        {t('outputMapper.visualEditor.cancel')}
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} type="button">
                        {t('outputMapper.visualEditor.addField')}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4">
                {mode === 'custom_input' && (
                    <div>
                        <label className="block text-sm font-medium text-content-strong mb-1">
                            {t('outputMapper.visualEditor.selectParentPath')}
                        </label>
                        <SelectField
                            value={selectedPath}
                            onValueChange={setSelectedPath}
                            options={pathOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
                            placeholder={t('outputMapper.visualEditor.selectParentPathPlaceholder')}
                            modal
                        />
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-content-strong mb-1">
                        {t(
                            mode === 'output'
                                ? 'outputMapper.visualEditor.fieldTarget'
                                : 'outputMapper.visualEditor.customFieldName'
                        )}
                    </label>
                    {targetOptions.length > 0 ? (
                        <TargetFieldSelect
                            options={targetOptions}
                            value={fieldName}
                            onChange={(target) => {
                                setFieldName(target);
                                setError(null);
                            }}
                            placeholder={t('outputMapper.visualEditor.selectTargetField')}
                            modal
                        />
                    ) : (
                        <StringField
                            value={fieldName}
                            onValueChange={(v) => {
                                setFieldName(v);
                                setError(null);
                            }}
                            placeholder={t(
                                mode === 'output'
                                    ? 'outputMapper.visualEditor.fieldTargetPlaceholder'
                                    : 'outputMapper.visualEditor.fieldNamePlaceholder'
                            )}
                        />
                    )}
                    {mode === 'output' && targetOptions.length === 0 && (
                        <p className="text-xs text-content-weak mt-1">
                            {t('outputMapper.visualEditor.fieldTargetHelp')}
                        </p>
                    )}
                    {error && <p className="text-sm text-status-danger-medium mt-1">{error}</p>}
                </div>
                {mode === 'custom_input' && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-content-strong mb-1">
                                {t('outputMapper.visualEditor.customFieldType')}
                            </label>
                            <SelectField
                                value={fieldType}
                                onValueChange={(value) => setFieldType(value as FieldType)}
                                options={fieldTypes}
                                modal
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-content-strong mb-1">
                                {t('outputMapper.visualEditor.customFieldDescription')}
                            </label>
                            <StringField
                                value={fieldDescription}
                                onValueChange={setFieldDescription}
                                placeholder={t('outputMapper.visualEditor.customFieldDescriptionPlaceholder')}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox checked={isArray} onChange={(e) => setIsArray(e.target.checked)} />
                            <label className="text-sm text-content-strong">
                                {t('outputMapper.visualEditor.customFieldIsArray')}
                            </label>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default AddFieldDialog;
